import React, { useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiLayers, FiX } from 'react-icons/fi';
import { LARGE_MOVE_THRESHOLD } from '../utils/groupChanges';

// Confirmation before any bulk group change (move into a group or remove from a group). It shows what will
// really happen: how many devices change, which of them are ALREADY in another group (grouped by that group,
// names expandable) and which are already in the target. When devices come from other groups the operator
// chooses "Move all" or "Only devices without a group". Large moves (> LARGE_MOVE_THRESHOLD, or the whole fleet)
// must be confirmed by typing the number of devices.

const deviceLabel = (devices, id) => {
  const d = devices[id] || {};
  return d.name || d.business_name || d.customer_name || id;
};

const groupLabel = (groupList, key) =>
  (key && groupList[key]?.name) || (key ? decodeURIComponent(key) : 'No group');

export default function GroupMoveConfirmModal({ isOpen, onClose, onConfirm, plan, devices, groupList, totalDevices }) {
  const [mode, setMode] = useState('all');
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode('all');
      setTyped('');
      setOpen({});
      setBusy(false);
    }
  }, [isOpen]);

  if (!isOpen || !plan) return null;

  const isRemove = !plan.target;
  const targetName = groupLabel(groupList, plan.target);
  const otherGroups = Object.entries(plan.fromOtherGroups).sort((a, b) => b[1].length - a[1].length);
  const fromOthersCount = otherGroups.reduce((n, [, ids]) => n + ids.length, 0);
  const count = mode === 'ungrouped' ? plan.ungrouped.length : plan.moving.length;
  const needsTyping = count > LARGE_MOVE_THRESHOLD || (totalDevices > 0 && count >= totalDevices);
  const typedOk = !needsTyping || typed.trim() === String(count);
  const canConfirm = count > 0 && typedOk && !busy;

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await onConfirm(mode);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-chrome w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${fromOthersCount ? 'bg-amber-500/15 text-amber-400' : 'brand-icon-badge'}`}>
              {fromOthersCount ? <FiAlertTriangle /> : <FiLayers />}
            </div>
            <h3 className="text-lg font-semibold text-white">
              {isRemove ? 'Remove devices from their group' : `Move devices to "${targetName}"`}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="overflow-y-auto pr-1 space-y-3 text-sm">
          <p className="text-gray-300">
            <span className="text-white font-semibold">{plan.moving.length}</span> of the selected devices will change group
            {plan.unchanged.length > 0 && (
              <span className="text-gray-500"> · {plan.unchanged.length} already in this group (no change)</span>
            )}
          </p>

          {plan.ungrouped.length > 0 && !isRemove && (
            <div className="card-inset p-3 text-gray-300">
              <span className="text-white font-semibold">{plan.ungrouped.length}</span> without a group today
            </div>
          )}

          {fromOthersCount > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-amber-300 font-medium mb-2">
                {fromOthersCount} device{fromOthersCount === 1 ? ' is' : 's are'} already in another group
                {isRemove ? ' and will lose it:' : ' and will be taken out of it:'}
              </p>
              <div className="space-y-1">
                {otherGroups.map(([key, ids]) => (
                  <div key={key}>
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                      className="w-full flex items-center justify-between gap-2 text-left text-gray-200 hover:text-white"
                    >
                      <span className="flex items-center gap-1.5">
                        {open[key] ? <FiChevronDown /> : <FiChevronRight />}
                        {groupLabel(groupList, key)}
                      </span>
                      <span className="text-gray-400">{ids.length}</span>
                    </button>
                    {open[key] && (
                      <ul className="ml-6 mt-1 mb-2 space-y-0.5 text-xs text-gray-400">
                        {ids.map((id) => (
                          <li key={id} className="truncate">
                            {deviceLabel(devices, id)} <span className="font-mono text-gray-600">{id}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {fromOthersCount > 0 && !isRemove && plan.ungrouped.length > 0 && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-gray-200">
                <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} />
                Move all {plan.moving.length} devices
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-gray-200">
                <input type="radio" checked={mode === 'ungrouped'} onChange={() => setMode('ungrouped')} />
                Only the {plan.ungrouped.length} devices without a group (keep the others where they are)
              </label>
            </div>
          )}

          {needsTyping && count > 0 && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3">
              <p className="text-red-300 mb-2">
                This changes the group of <span className="font-semibold">{count}</span> devices
                {totalDevices > 0 && count >= totalDevices ? ' — the whole fleet' : ''}. Type <span className="font-mono font-semibold">{count}</span> to confirm.
              </p>
              <input
                autoFocus
                inputMode="numeric"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="input-field"
                placeholder={String(count)}
              />
            </div>
          )}

          <p className="text-xs text-gray-500">
            Every change is saved in the group history (Groups page) and can be reverted. You can also undo it right after from the notification.
          </p>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={confirm}
            disabled={!canConfirm}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Saving…' : isRemove ? `Remove ${count} device${count === 1 ? '' : 's'}` : `Move ${count} device${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
